import store from '@/store';

export const WEB_AI_WIDGET_APP_CODE_NAME = 'webaiwidgetmgr';
export const WEB_AI_WIDGET_FEATURE_FLAGS = [
  'rollout.online_presence.web_ai_widget',
];
export const FEATURE_FLAGS_TIMEOUT = 5000;

const areFeatureFlagsLoaded = () => store.getters['BusinessStore/featureFlagsLoaded'];

export const isWebAiWidgetFeatureEnabled = () => {
  if (!areFeatureFlagsLoaded()) return false;

  const hasFeature = store.getters['BusinessStore/hasFeature'];
  return WEB_AI_WIDGET_FEATURE_FLAGS.some((featureName) => hasFeature(featureName));
};

// Resolved from the v3 AppsStore (assignment_type installed ∪ internal), matching
// the webaiwidgetserver install gate. Synchronous cache read: only meaningful once
// AppsStore has loaded — returns false until then (callers that need a definitive
// answer use the async `isWebAiWidgetInstalled`).
export const isWebAiWidgetInstalledInCache = () => {
  if (!store.getters['AppsStore/appsLoaded']) return false;
  return store.getters['AppsStore/isAppInstalledOrEntitled'](WEB_AI_WIDGET_APP_CODE_NAME);
};

export const isWebAiWidgetInstalled = async () => {
  // loadApps is cached (loaded flag) and de-duped (in-flight promise), so this is a
  // no-op network-wise once the v3 apps list is warm.
  if (!store.getters['AppsStore/appsLoaded']) {
    await store.dispatch('AppsStore/loadApps');
  }
  return store.getters['AppsStore/isAppInstalledOrEntitled'](WEB_AI_WIDGET_APP_CODE_NAME);
};

const waitForFeatureFlagsWithTimeout = async () => {
  if (areFeatureFlagsLoaded()) {
    return true;
  }

  let timeoutId;
  let unwatch;
  try {
    await Promise.race([
      new Promise((resolve) => {
        unwatch = store.watch(
          (state, getters) => getters['BusinessStore/featureFlagsLoaded'],
          (loaded) => {
            if (loaded) resolve();
          },
        );
      }),
      new Promise((resolve) => {
        timeoutId = setTimeout(resolve, FEATURE_FLAGS_TIMEOUT);
      }),
    ]);
  } finally {
    // Always release both resources, regardless of which promise won the race.
    if (timeoutId) clearTimeout(timeoutId);
    if (unwatch) unwatch();
  }

  return areFeatureFlagsLoaded();
};

export const isWebAiWidgetAvailable = async () => {
  const featureFlagsLoaded = await waitForFeatureFlagsWithTimeout();

  if (featureFlagsLoaded && isWebAiWidgetFeatureEnabled()) {
    return true;
  }

  return isWebAiWidgetInstalled();
};
